import { memo, useCallback, useLayoutEffect, useState } from 'react'

import { GroupAdd, Refresh } from '@mui/icons-material'
import { Button, Tooltip } from '@mui/material'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, ListOperationBox } from '@shared/layouts'
import { UpdateFunctionProps, useColumns, RowActions } from 'domains/Organizations/Organization/AccessSettings'
import { TeamDialogContent } from 'domains/Organizations/Organization/AccessSettings/Teams'
import { useOrganizationMemberUpdateMutation } from 'hooks/mutations/org'
import { teamsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Members'
import { organizationQueryKeys } from 'services/queryKeys'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const { organizationId } = teamsOverviewRoute.useParams()
  const navigate = useNavigate({ from: teamsOverviewRoute.fullPath })
  const { query } = teamsOverviewRoute.useSearch()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const environmentMemberTeamsQuery = useInfiniteQuery({
    ...organizationQueryKeys.memberList({ organizationId, memberType: 'Team', query }),
    refetchOnMount: 'always',
  })
  const teamMembers = environmentMemberTeamsQuery.data?.pages.flatMap((page) => page.items)

  const { mutate: updateUser, ...organizationMemberUpdateMutation } = useOrganizationMemberUpdateMutation()

  const handleUpdate = useCallback(
    ({ memberId, roles }: UpdateFunctionProps) => {
      updateUser({
        organizationId,
        memberId,
        organizationRoleRamMemberEdit: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          roles,
        },
      })
    },
    [organizationId, updateUser],
  )

  const columns = useColumns({
    onUpdate: handleUpdate,
  })

  useLayoutEffect(() => {
    if (organizationMemberUpdateMutation.isPending) {
      setLoadingRows([organizationMemberUpdateMutation.variables.memberId])

      return () => setLoadingRows([])
    }
  }, [organizationMemberUpdateMutation.isPending, organizationMemberUpdateMutation.variables?.memberId])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:My.Organization.searchTeams')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <ListOperationBox>
          <Tooltip title={t('refresh')} placement="left">
            <AnimatedCounterButton
              action={useCallback(
                () =>
                  queryClient.invalidateQueries({
                    queryKey: organizationQueryKeys.memberList({ organizationId, memberType: 'User' }).queryKey,
                  }),
                [organizationId, queryClient],
              )}
            >
              {(props) => <Refresh {...props} />}
            </AnimatedCounterButton>
          </Tooltip>

          <Button variant="contained" endIcon={<GroupAdd />} onClick={() => setDialogOpen(true)}>
            {t('domain:My.Organization.addTeam')}
          </Button>
        </ListOperationBox>
      </ActionStrip>

      <Table
        data={teamMembers ?? []}
        columns={columns}
        isInitialLoadingGlobal={environmentMemberTeamsQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: ['roles'] }), {})}
        ActionMenuProps={(row) => ({
          title: row.original.teamInfo?.name || '',
          entity: t('entity:org.team', 'Team', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (member) => member.id,
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <TeamDialogContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
